import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Skeleton,
  Link as MuiLink,
  Alert,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Warning,
  Link,
  CalendarToday,
  LocationOn,
  Tag,
  OpenInNew,
  Assignment
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { setSelectedVendor, fetchVendorDetails, setSortBy, searchVendors } from '../../store/slices/vendorSlice';
// TODO: Implement OFAC slice
// import { screenVendor, batchScreenVendors, selectOFACScreeningResult } from '../../store/slices/ofacSlice';
import { fraudUtils } from '../../services/api';
import RiskScoreBadge from './RiskScoreBadge';
import FraudIndicatorChips from './FraudIndicatorChips';
// TODO: Implement OFAC components
// import OFACStatusBadge from './OFACStatusBadge';
// import OFACSanctionAlert from './OFACSanctionAlert';

// Wrapper component for OFAC badge to handle useAppSelector hook - TODO: Implement
// const OFACStatusBadgeWrapper: React.FC<{ vendor: any }> = ({ vendor }) => {
//   const dispatch = useAppDispatch();
//   const screeningResult = useAppSelector(state => selectOFACScreeningResult(state, vendor.uei_sam));
//   const { bulkScreeningLoading } = useAppSelector(state => state.ofac);

//   const handleScreen = () => {
//     dispatch(screenVendor({
//       uei: vendor.uei_sam,
//       name: vendor.legal_business_name
//     }));
//   };

//   return (
//     <OFACStatusBadge
//       screeningResult={screeningResult}
//       loading={bulkScreeningLoading}
//       onClick={handleScreen}
//       size="small"
//     />
//   );
// };

const VendorSearchResults: React.FC = () => {
  const dispatch = useAppDispatch();
  const { searchResults, loading, totalFound, searchQuery, sortBy, searchType } = useAppSelector(state => state.vendor);
  // TODO: Implement OFAC state
  // const { settings: ofacSettings, bulkScreeningLoading } = useAppSelector(state => state.ofac);
  // OFAC screening is always enabled by default - no toggle or state needed

  const handleViewDetails = (vendor: any) => {
    dispatch(setSelectedVendor(vendor));
    dispatch(fetchVendorDetails(vendor.uei_sam));
  };

  // OFAC screening is automatic - no manual handlers needed

  // Auto-screen vendors when results load if enabled
  // TODO: Implement auto-screening when OFAC is ready
  // useEffect(() => {
  //   if (autoScreenEnabled && searchResults.length > 0 && !loading) {
  //     // Only auto-screen first 10 results to avoid overwhelming the system
  //     const vendorsToScreen = searchResults.slice(0, 10).map(vendor => ({
  //       uei: vendor.uei_sam,
  //       name: vendor.legal_business_name
  //     }));
  //     dispatch(batchScreenVendors(vendorsToScreen));
  //   }
  // }, [autoScreenEnabled, searchResults, loading, dispatch]);

  const handleSortChange = (newSortBy: 'relevance' | 'award_count') => {
    dispatch(setSortBy(newSortBy));
    // Re-search with new sorting
    if (searchQuery) {
      dispatch(searchVendors({
        query: searchQuery,
        search_type: searchType,
        limit: 50,
        sort_by: newSortBy
      }));
    }
  };

  const formatMatchScore = (score: number) => {
    if (score >= 90) return { color: 'success', label: 'Excellent Match' };
    if (score >= 70) return { color: 'info', label: 'Good Match' };
    if (score >= 50) return { color: 'warning', label: 'Partial Match' };
    return { color: 'error', label: 'Weak Match' };
  };

  // OFAC monitoring - TODO: Implement when OFAC state is available
  // const ofacState = useAppSelector(state => state.ofac);
  // useEffect(() => {
  //   // Check for new sanctions matches and show alert
  //   const sanctionedResults = Object.values(ofacState.screeningResults).filter(
  //     result => result.matchStatus === 'sanctioned' || result.matchStatus === 'possible_match'
  //   );

  //   // Show alert for the most recent sanctioned result
  //   if (sanctionedResults.length > 0) {
  //     const latestSanction = sanctionedResults.reduce((latest, current) =>
  //       new Date(current.screenedAt) > new Date(latest.screenedAt) ? current : latest
  //     );

  //     // Only show alert if it's a new result (within last 5 seconds)
  //     const isRecent = Date.now() - new Date(latestSanction.screenedAt).getTime() < 5000;
  //     if (isRecent && !sanctionAlertOpen) {
  //       setAlertVendorResult(latestSanction);
  //       setSanctionAlertOpen(true);
  //     }
  //   }
  // }, [ofacState.screeningResults, sanctionAlertOpen]);

  // Loading state
  if (loading) {
    return (
      <Box>
        <LinearProgress sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Searching vendors...
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={32} />
                  <Skeleton variant="text" width="60%" height={24} sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Skeleton variant="rectangular" width={60} height={20} />
                    <Skeleton variant="rectangular" width={80} height={20} />
                  </Box>
                  <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
                </CardContent>
                <CardActions>
                  <Skeleton variant="rectangular" width="100%" height={36} />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // No results after search
  if (searchQuery && searchResults.length === 0 && totalFound === 0 && !loading) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          No vendors found
        </Typography>
        <Typography variant="body2">
          No vendors were found matching "{searchQuery}". Try:
        </Typography>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Checking the spelling of the vendor name</li>
          <li>Using a shorter or more general search term</li>
          <li>Trying a different search type (name vs UEI)</li>
        </ul>
      </Alert>
    );
  }

  // Show results if we have any
  if (searchResults.length === 0) {
    return null;
  }

  return (
    <Box>
      {/* Results Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6">
            Found {totalFound} vendor{totalFound !== 1 ? 's' : ''}
            {searchQuery && ` matching "${searchQuery}"`}
          </Typography>
          {totalFound > searchResults.length && (
            <Typography variant="body2" color="text.secondary">
              Showing {searchResults.length} of {totalFound} results
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Sort Control */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => handleSortChange(e.target.value as 'relevance' | 'award_count')}
              disabled={loading}
            >
              <MenuItem value="relevance">Relevance</MenuItem>
              <MenuItem value="award_count">Award Count</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Results Grid */}
      <Grid container spacing={3}>
        {searchResults.map((vendor, index) => {
          const matchScore = formatMatchScore(vendor.match_score || 0);
          return (
            <Grid item xs={12} sm={6} lg={4} key={vendor.uei_sam || index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  {/* Vendor Name & Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="div" sx={{ flex: 1, lineHeight: 1.3 }}>
                      {vendor.legal_business_name}
                    </Typography>
                    <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      {vendor.registration_status === 'Active' ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Active"
                          color="success"
                          size="small"
                          variant="filled"
                        />
                      ) : (
                        <Chip
                          icon={<Warning />}
                          label={vendor.registration_status || 'Unknown'}
                          color="warning"
                          size="small"
                          variant="filled"
                        />
                      )}
                      {/* OFAC Status Badge - TODO: Implement */}
                      {/* <OFACStatusBadgeWrapper vendor={vendor} /> */}
                      {vendor.risk_analysis && (
                        <RiskScoreBadge
                          score={vendor.risk_analysis.risk_score}
                          level={vendor.risk_analysis.risk_level}
                          size="small"
                        />
                      )}
                      {vendor.match_score && (
                        <Chip
                          label={matchScore.label}
                          color={matchScore.color as any}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* UEI & CAGE */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>
                      UEI: {vendor.uei_sam}
                    </Typography>
                    {vendor.cage_code && (
                      <Typography variant="body2" color="text.secondary">
                        CAGE: {vendor.cage_code}
                      </Typography>
                    )}
                  </Box>

                  {/* Additional Info Grid */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Award Count - Display prominently */}
                    {vendor.award_count !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assignment sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {vendor.award_count} Awards
                        </Typography>
                      </Box>
                    )}

                    {vendor.registration_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Registered: {fraudUtils.formatDate(vendor.registration_date)}
                        </Typography>
                      </Box>
                    )}

                    {(vendor.city || vendor.state_code) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {[vendor.city, vendor.state_code].filter(Boolean).join(', ').toUpperCase()}
                        </Typography>
                      </Box>
                    )}

                    {vendor.primary_naics && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tag sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          NAICS: {vendor.primary_naics}
                        </Typography>
                      </Box>
                    )}

                    {vendor.entity_url && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <MuiLink
                          href={vendor.entity_url.startsWith('http') ? vendor.entity_url : `https://${vendor.entity_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          color="primary"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {vendor.entity_url}
                          <OpenInNew sx={{ fontSize: 12 }} />
                        </MuiLink>
                      </Box>
                    )}
                  </Box>

                  {/* Risk Analysis Section */}
                  {vendor.risk_analysis && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      {vendor.risk_analysis.risk_indicators && vendor.risk_analysis.risk_indicators.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Risk Indicators:
                          </Typography>
                          <FraudIndicatorChips
                            indicators={vendor.risk_analysis.risk_indicators}
                            maxDisplay={2}
                            showDetails={false}
                          />
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Click "View Details & Awards" to see full risk and competition analysis
                      </Typography>
                    </Box>
                  )}

                  {/* Match Score */}
                  {vendor.match_score && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Match Score: {vendor.match_score.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={vendor.match_score}
                        sx={{
                          mt: 0.5,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: vendor.match_score >= 70 ? 'success.main' :
                                           vendor.match_score >= 50 ? 'warning.main' : 'error.main'
                          }
                        }}
                      />
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Visibility />}
                    onClick={() => handleViewDetails(vendor)}
                    size="large"
                  >
                    View Details & Awards
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Show more results hint */}
      {totalFound > searchResults.length && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing top {searchResults.length} most relevant results.
            Try refining your search for more specific results.
          </Typography>
        </Box>
      )}

      {/* OFAC Sanction Alert Dialog - TODO: Implement when OFACSanctionAlert component is ready */}
      {/* {alertVendorResult && (
        <OFACSanctionAlert
          open={sanctionAlertOpen}
          onClose={() => {
            setSanctionAlertOpen(false);
            setAlertVendorResult(null);
          }}
          screeningResult={alertVendorResult}
          onAcknowledge={() => {
            // Handle acknowledgment logging if needed
            console.log('OFAC alert acknowledged for:', alertVendorResult.vendorUei);
          }}
          onReportFalsePositive={() => {
            // This will be handled by the OFACSanctionAlert component internally
            console.log('False positive reported for:', alertVendorResult.vendorUei);
          }}
        />
      )} */}
    </Box>
  );
};

export default VendorSearchResults;